// src/components/SurveyCard.jsx
import React, { useState } from "react"
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  ListGroup,
  ListGroupItem,
} from "reactstrap"
import { Link, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import PrimaryButton from "newcomponents/primary-button"
import AddIcon from "@mui/icons-material/Add"

const BLUE = "#2990DF"

const SurveyCard = ({ surveys = [] }) => {
  const navigate = useNavigate()
  const mode = useSelector(s => s.Layout?.layoutMode)
  const userData = useSelector(state => state.Login.userData)

  const [currentPage, setCurrentPage] = useState(1)
  const [selectedSurvey, setSelectedSurvey] = useState(null)

  const surveysPerPage = 5
  const validSurveys = Array.isArray(surveys) ? surveys : []

  const indexOfLastSurvey = currentPage * surveysPerPage
  const indexOfFirstSurvey = indexOfLastSurvey - surveysPerPage
  const currentSurveys = validSurveys.slice(
    indexOfFirstSurvey,
    indexOfLastSurvey,
  )
  const totalPages = Math.max(
    1,
    Math.ceil(validSurveys.length / surveysPerPage),
  )

  const paginate = pageNumber => {
    if (pageNumber > 0 && pageNumber <= totalPages) setCurrentPage(pageNumber)
  }

  const openSurveyModal = survey => setSelectedSurvey(survey)
  const closeModal = () => setSelectedSurvey(null)

  // theme tokens (why: consistent light/dark modal styling)
  const isDark = mode === "dark"
  const C = {
    headerBg: isDark ? "#111827" : "#F9FAFB",
    title: isDark ? "#F9FAFB" : "#111827",
    border: isDark ? "#374151" : "#E5E7EB",
    contentBg: isDark ? "#1F2937" : "#FFFFFF",
    cardBg: isDark ? "#0F172A" : "#FFFFFF",
    questionBg: isDark ? "#0B1220" : "#F9FAFB",
    textSubtle: isDark ? "#9CA3AF" : "#6B7280",
  }

  return (
    <>
      {/* Pagination active: blue */}
      <style>{`
        .pagination .page-item.active .page-link {
          background-color: ${BLUE};
          border-color: ${BLUE};
          color: #fff;
        }
        .pagination .page-link:focus {
          box-shadow: 0 0 0 0.25rem rgba(41,144,223,0.25);
        }
        /* Modal polish */
        .survey-modal .modal-content {
          border-radius: 16px;
          border: 1px solid ${C.border};
          background-color: ${C.contentBg};
          box-shadow: 0 20px 40px rgba(0,0,0,0.25);
        }
        .survey-modal .modal-header {
          background: ${C.headerBg};
          border-bottom: 1px solid ${C.border};
          padding: 14px 18px;
        }
        .survey-modal .modal-title, .survey-modal .modal-header h4 {
          color: ${C.title};
          font-weight: 700;
          font-size: 1.125rem;
          margin: 0;
        }
        .survey-modal .btn-close {
          filter: ${isDark ? "invert(1) grayscale(100%)" : "none"};
          opacity: 0.8;
        }
        .survey-modal .modal-body {
          padding: 16px 18px;
          max-height: 70vh;
          overflow: auto;
        }
        .survey-modal .modal-body::-webkit-scrollbar {
          width: 10px;
        }
        .survey-modal .modal-body::-webkit-scrollbar-thumb {
          background: ${isDark ? "#2D3748" : "#CBD5E1"};
          border-radius: 8px;
        }
        .survey-modal .category-card {
          background: ${C.cardBg};
          border: 1px solid ${C.border};
          border-radius: 12px;
          padding: 12px 14px;
          margin-bottom: 14px;
        }
        .survey-modal .category-title {
          display: flex;
          align-items: center;
          gap: 8px;
          color: ${C.title};
          font-weight: 700;
          margin: 0 0 8px 0;
        }
        .survey-modal .category-divider {
          border: none;
          border-top: 1px solid ${C.border};
          margin: 8px 0 10px;
        }
        .survey-modal .question {
          background: ${C.questionBg};
          border: 1px solid ${C.border};
          border-radius: 10px;
          padding: 10px 12px;
          margin-bottom: 8px;
        }
        .survey-modal .question h6 {
          margin: 0 0 6px 0;
          color: ${C.title};
          font-weight: 700;
          font-size: 0.95rem;
        }
        .survey-modal .question .badge {
          border-radius: 999px;
          font-weight: 600;
          padding: 0.35rem 0.6rem;
        }
        .survey-modal .question ul {
          margin: 6px 0 0 0;
        }
        .survey-modal .muted {
          color: ${C.textSubtle};
        }
      `}</style>

      <Card
        className="br"
        style={{
          height: "100%",
          backgroundColor: mode === "dark" ? "#252F40" : "#fff",
        }}
      >
        <CardBody
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <Row className="align-items-center mb-3">
            <Col xl={6} lg={6} md={6} className="d-flex align-items-center">
              <CardTitle
                className="h2 mb-0 text-uppercase"
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: isDark ? "white" : "#1F2937",
                }}
              >
                Surveys
              </CardTitle>
            </Col>
            <Col
              xl={6}
              lg={6}
              md={6}
              className="d-flex justify-content-end align-items-center"
            >
              {userData.role !== "viewer" && (
                <PrimaryButton
                  style={{
                    padding: "10px 20px",
                    fontSize: "16px",
                    fontWeight: "400",
                    borderRadius: "8px",
                  }}
                  icon={<AddIcon />}
                  onClick={() => navigate("/dashboard/create-survey")}
                >
                  Create Survey
                </PrimaryButton>
              )}
            </Col>
          </Row>

          <div style={{ height: "0.1px", width: "100%", background: "gray" }} />

          {/* List (matches Audience) */}
          <div
            className="inbox-wid table-responsive"
            style={{ minHeight: "10vh", flex: 1 }}
          >
            {currentSurveys.length > 0 ? (
              currentSurveys.map((survey, key) => (
                <Link
                  key={key}
                  to="#"
                  className="text-dark"
                  onClick={() => openSurveyModal(survey)}
                >
                  <div className="inbox-item">
                    <h6 className="inbox-item-author mb-1 font-size-14">
                      {survey.title}
                    </h6>
                    <p
                      className="inbox-item-text text-muted mb-0"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "200px",
                      }}
                    >
                      {survey.description}
                    </p>
                  </div>
                </Link>
              ))
            ) : (
              <p className="muted">No surveys available.</p>
            )}
          </div>

          {/* Pagination pinned bottom */}
          <Pagination
            className="mt-3 justify-content-center"
            style={{ marginTop: "auto" }}
          >
            {/* Previous Button */}
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink
                style={{
                  color: mode === "dark" ? "#fff" : "#000",
                  backgroundColor: "transparent",
                  borderColor: mode === "dark" ? "#444" : "#dee2e6",
                }}
                previous
                onClick={() => paginate(currentPage - 1)}
              />
            </PaginationItem>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }).map((_, i) => (
              <PaginationItem active={i + 1 === currentPage} key={i}>
                <PaginationLink
                  style={{
                    borderColor:
                      i + 1 === currentPage
                        ? "#2990DF"
                        : mode === "dark"
                          ? "#444"
                          : "#dee2e6",
                    boxShadow: "none",
                    backgroundColor:
                      i + 1 === currentPage ? "#2990DF" : "transparent",
                    color:
                      i + 1 === currentPage
                        ? "#fff"
                        : mode === "dark"
                          ? "#fff"
                          : "#000",
                  }}
                  onClick={() => paginate(i + 1)}
                >
                  {i + 1}
                </PaginationLink>
              </PaginationItem>
            ))}

            {/* Next Button */}
            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink
                style={{
                  color: mode === "dark" ? "#fff" : "#000",
                  backgroundColor: "transparent",
                  borderColor: mode === "dark" ? "#444" : "#dee2e6",
                }}
                next
                onClick={() => paginate(currentPage + 1)}
              />
            </PaginationItem>
          </Pagination>
        </CardBody>
      </Card>

      {/* Polished Survey Modal */}
      {selectedSurvey && (
        <Modal
          isOpen
          toggle={closeModal}
          size="lg"
          centered
          scrollable
          className="survey-modal"
        >
          <ModalHeader toggle={closeModal}>
            <h4 className="mb-0">Survey Details</h4>
          </ModalHeader>

          <ModalBody>
            <div className="mb-3">
              <h5 style={{ margin: 0, color: C.title, fontWeight: 700 }}>
                {selectedSurvey.title}
              </h5>
              <p className="muted mb-0">{selectedSurvey.description}</p>
            </div>

            {selectedSurvey.SurveyCategories?.length ? (
              selectedSurvey.SurveyCategories.map((category, index) => (
                <div key={index} className="category-card">
                  <div className="category-title">
                    {category.name}
                    <Badge color="info" pill>
                      {category.SurveyQuestions?.length ?? 0}
                    </Badge>
                  </div>
                  <hr className="category-divider" />

                  <ListGroup flush>
                    {category.SurveyQuestions.map((question, qIndex) => (
                      <ListGroupItem key={qIndex} className="question">
                        <h6>
                          Q{qIndex + 1}. {question.questionText}
                        </h6>

                        <Badge
                          color={
                            question.questionType === "rating"
                              ? "warning"
                              : question.questionType === "multiple choice"
                                ? "success"
                                : "secondary"
                          }
                          className="mb-2"
                          pill
                        >
                          {question.questionType
                            .replace("_", " ")
                            .toUpperCase()}
                        </Badge>

                        {question.questionType === "multiple choice" && (
                          <ul className="pl-3">
                            {question.options.map((option, optIndex) => (
                              <li key={optIndex} className="muted">
                                {option}
                              </li>
                            ))}
                          </ul>
                        )}
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </div>
              ))
            ) : (
              <p className="muted text-center mb-0">
                No questions available for this survey.
              </p>
            )}
          </ModalBody>
        </Modal>
      )}
    </>
  )
}

export default SurveyCard
